import { isNullOrEmpty, paymentTypes } from './constants';


export class Validate {


  /**
    * Validates required fields for IBAN form
    */
  static requiredFieldsForIban(iban, beneficiaryName, purpose, currency) {
    const missingFields = [];

    if (!purpose || purpose.trim() === "") {
      missingFields.push("Purpose");
    }

    if (!currency || currency.trim() === "") {
      missingFields.push("Currency");
    }

    if (!iban || iban.trim() === "") {
      missingFields.push("IBAN Account Number");
    }

    if (!beneficiaryName || beneficiaryName.trim() === "") {
      missingFields.push("Beneficiary Name");
    }
    return missingFields;
  }


  /**
    * Validates that a string doesn't contain hyphens
    */
  static noHyphens(value, fieldName = "Field") {
    if (value && value.indexOf("-") !== -1) {
      return {
        isValid: false,
        message: `Hyphens (-) are not allowed in ${fieldName}. Please remove the hyphen and try again.`
      };
    }
    return { isValid: true };
  }

  /**
 * Validates IBAN requirements
 */
  static iban(paymentType, iban) {
    if (paymentType === paymentTypes.IBAN) {
      return this.required(iban, "IBAN");
    }
    return { isValid: true };
  }




  /**
  * Validates SWIFT account number requirements
  */
  static swiftAccountNumber(paymentType, accountNumber) {
    if (paymentType === paymentTypes.SWIFT) {
      return this.required(accountNumber, "Account Number for SWIFT payment type");
    }
    return { isValid: true };
  }



  /**
  * Validates if a field is required and not empty
  */
  static required(value, fieldName) {
    if (isNullOrEmpty(value)) {
      return {
        isValid: false,
        message: `${fieldName} is required`
      };
    }
    return { isValid: true };
  }

/**
 * Validates that a string doesn't contain ampersands (&)
 */
  static noAmpersands(value, fieldName = "Field") {
    if (value && value.indexOf("&") !== -1) {
      return {
        isValid: false,
        message: `Ampersand (&) is not allowed in ${fieldName}`
      };
    }
    return { isValid: true };
  }


    /**
   * Validates text against a regex pattern
   */
  static pattern(value, pattern, errorMessage) {
    if (value && pattern.test(value)) {
      return {
        isValid: false,
        message: errorMessage
      };
    }
    return { isValid: true };
  }
  

  /**
   * Validates field length
   */
  static length(value, requiredLength, fieldName) {
    if (value && value.length < requiredLength || value.length > requiredLength) {
      return {
        isValid: false,
        message: `${fieldName} must have exactly ${requiredLength} numeric characters`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates exact length
   */
  static exactLength(value, exactLength, fieldName) {
    if (value && value.length !== exactLength) {
      return {
        isValid: false,
        message: `${fieldName} requires ${exactLength} characters`
      };
    }
    return { isValid: true };
  }


  /**
  * Validates required fields for REGNO/ACCOUNT form
  */
  static requiredFieldsForRegnoAccount(regNo, accountNumber) {
    const missingFields = [];

    if (!regNo || regNo.trim() === "") {
      missingFields.push("Registration Number");
    }

    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    return missingFields;
  }


  /**
  * Validates that SWIFT values are unique and returns validation result
  */
  static swiftDifferentValues(accountNumber, swift, correspondingBicSwift) {
    return this.uniqueValues(
      [accountNumber, swift, correspondingBicSwift],
      ["Account Number", "Beneficiary Bank SWIFT BIC", "Corresponding Bank SWIFT BIC"]
    );
  }


  /**
   * Validates required fields for SWIFT form
   */
  static requiredFieldsForSwift(currency, accountNumber, bic, beneficiaryName) {
    const missingFields = [];

    // Check Currency
    if (!currency || currency.trim() === "") {
      missingFields.push("Currency");
    }

    // Check Account Number
    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    // Check Beneficiary Bank SWIFT BIC
    if (!bic || bic.trim() === "") {
      missingFields.push("Beneficiary Bank SWIFT BIC");
    }

    // Check Beneficiary Name
    if (!beneficiaryName || beneficiaryName.trim() === "") {
      missingFields.push("Beneficiary Name");
    }

    return missingFields;
  }

  /**
   * Validates required fields for Regno/Account (DK) before validation
   */
  static requiredFieldsForRegnoAccount(regNo, accountNumber) {
    const missingFields = [];

    // Check Registration Number
    if (!regNo || regNo.trim() === "") {
      missingFields.push("Registration Number");
    }

    // Check Account Number
    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    return missingFields;
  }

  /**
   * Validates required fields for ABA Routing Number before validation (only ABA number needed)
   */
  static requiredFieldsForABAValidation(abaRoutingNumber) {
    const missingFields = [];

    // Check ABA Routing Number
    if (!abaRoutingNumber || abaRoutingNumber.trim() === "") {
      missingFields.push("ABA Routing Number");
    }

    return missingFields;
  }

  /**
   * Validates required fields for ABA Routing Number when adding bank data
   */
  static requiredFieldsForABAAdd(currency, accountType, accountNumber, beneficiaryName, abaRoutingNumber) {
    const missingFields = [];

    // Check Currency
    if (!currency || currency.toString().trim() === "") {
      missingFields.push("Currency");
    }

    // Check Account Type
    if (!accountType || accountType.trim() === "") {
      missingFields.push("Account Type");
    }

    // Check Account Number
    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    // Check Beneficiary Name
    if (!beneficiaryName || beneficiaryName.trim() === "") {
      missingFields.push("Beneficiary Name");
    }

    // Check ABA Routing Number
    if (!abaRoutingNumber || abaRoutingNumber.trim() === "") {
      missingFields.push("ABA Routing Number");
    }

    return missingFields;
  }


  /**
   * Validates SWIFT BIC validation required fields and returns which fields are invalid
   */
  static swiftBicRequiredFields(bankAccountNumber, bic, regNo, showRegNo) {
    const invalidFields = [];

    if (!bankAccountNumber || bankAccountNumber.trim() === "") {
      invalidFields.push("bankAccountNumber");
    }

    if (showRegNo && (!regNo || regNo.trim() === "")) {
      invalidFields.push("regNo");
    }

    if (!bic || bic.trim() === "") {
      invalidFields.push("bic");
    }

    return {
      isValid: invalidFields.length === 0,
      invalidFields
    };
  }


  /**
 * Validates that Corresponding BIC SWIFT does not contain numbers
 */
  static correspondingBicNoNumbers(correspondingBic) {
    if (
      correspondingBic !== null &&
      correspondingBic.trim() !== "" &&
      /\d/.test(correspondingBic)
    ) {
      return {
        isValid: false,
        message: "Corresponding Bank SWIFT BIC must not contain numbers. Only alphanumeric characters are allowed."
      };
    }
    return { isValid: true };
  }

  /**
  * Validates that values are different from each other
  */
  static uniqueValues(values, valueNames) {
    const fieldValues = values.filter(v => v && v.trim() !== "");
    const fieldNames = valueNames.filter((name, index) => values[index] && values[index].trim() !== "");

    if (fieldValues.length <= 1) {
      return { isValid: true };
    }

    // Check if all non-empty values are the same
    if (fieldValues.length === 3 && fieldValues.every(v => v === fieldValues[0])) {
      return {
        isValid: false,
        message: `${fieldNames.join(", ")} cannot all have the same value.`
      };
    }

    // Check pairs
    for (let i = 0; i < fieldValues.length; i++) {
      for (let j = i + 1; j < fieldValues.length; j++) {
        if (fieldValues[i] === fieldValues[j]) {
          return {
            isValid: false,
            message: `${fieldNames[i]} and ${fieldNames[j]} cannot have the same value.`
          };
        }
      }
    }

    return { isValid: true };
  }


}